import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  createClientCreativeKnowledge,
  deleteClientCreativeKnowledge,
  fetchClientCreativeKnowledge,
  fetchClientOffers,
  fetchSharedVerticalCreativeAssets,
  updateClientCreativeKnowledge,
} from '@src/api';
import { fetchVerticals } from '@src/api/actions/verticals';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { useAuth } from '@src/context';

const EMPTY_SHARED_VERTICAL_ASSET = {
  id: undefined,
  vertical: '',
  general_asset_folder_url: '',
  recent_ad_examples_url: '',
};

const EMPTY_CLIENT_VERTICAL_ASSET = {
  id: undefined,
  vertical: '',
  client_asset_folder_url: '',
};

const BATH_MANUFACTURER_OPTIONS = [
  { value: 'bci', label: 'BCI' },
  { value: 'jacuzzi', label: 'Jacuzzi' },
  { value: 'kohler', label: 'Kohler' },
  { value: 'samuel_mueller', label: 'Samuel Mueller (BCI)' },
];

const EMPTY_RECORD = {
  client: '',
  bath_manufacturer: [],
  bath_manufacturer_label: '',
  bath_manufacturer_options: [],
  verticals: [],
  vertical_details: [],
  website_url: '',
  logo_url: '',
  notes: '',
  ai_script_instructions: '',
  client_vertical_assets: [],
  shared_vertical_assets: [],
};

function collectErrorMessages(value, prefix = '') {
  if (!value) {
    return [];
  }
  if (typeof value === 'string') {
    return [prefix ? `${prefix}: ${value}` : value];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => collectErrorMessages(item, prefix || `Item ${index + 1}`));
  }
  if (typeof value === 'object') {
    return Object.entries(value).flatMap(([key, nestedValue]) => {
      const nextPrefix = prefix ? `${prefix} > ${key}` : key;
      return collectErrorMessages(nestedValue, nextPrefix);
    });
  }
  return [prefix ? `${prefix}: ${String(value)}` : String(value)];
}

function getApiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length) {
    return String(data.non_field_errors[0]);
  }
  if (data && typeof data === 'object') {
    const firstValue = Object.values(data).find((value) => (Array.isArray(value) ? value.length > 0 : Boolean(value)));
    if (Array.isArray(firstValue) && firstValue.length) return String(firstValue[0]);
    if (typeof firstValue === 'string' && firstValue.trim()) return firstValue;
  }
  return fallback;
}

function getApiFieldErrors(err) {
  const data = err?.response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return {};
  }
  return data;
}

function normalizeUrl(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return '';
  }
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

function ExternalLink({ href, label }) {
  if (!href) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Button
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      size="small"
      startIcon={<LinkIcon fontSize="small" />}
      sx={{ px: 0.25, minWidth: 0, justifyContent: 'flex-start', lineHeight: 1.1 }}
    >
      {label}
    </Button>
  );
}

export default function ClientCreativeKnowledgePage() {
  const { state: authState } = useAuth();
  const authUser = authState?.user;
  const isStaff = authUser?.is_staff || authUser?.is_superuser;

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialClientId = String(location?.state?.clientId ?? searchParams.get('client_id') ?? '');

  const [rows, setRows] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [clientOffers, setClientOffers] = useState([]);
  const [filterClient, setFilterClient] = useState(initialClientId);
  const [filterVertical, setFilterVertical] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loadingSharedAssets, setLoadingSharedAssets] = useState(false);
  const [current, setCurrent] = useState(EMPTY_RECORD);

  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue: findClientOptionByValue,
    addOption: addClientOption,
  } = useClientAutocomplete();

  const loadVerticals = async () => {
    try {
      const res = await fetchVerticals();
      setVerticals(Array.isArray(res.data) ? res.data : []);
    } catch (_err) {
      // Keep page usable if verticals fail.
    }
  };

  const loadRows = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchClientCreativeKnowledge({
        client_id: filterClient || undefined,
        vertical_id: filterVertical || undefined,
        search: search || undefined,
      });
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
          ? res.data.results
          : [];
      data.forEach((row) => {
        if (row.client && row.client_name) {
          addClientOption({ value: row.client, label: row.client_name });
        }
      });
      setRows(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load creative knowledge'));
    } finally {
      setLoading(false);
    }
  };

  const loadClientOffers = async (clientId) => {
    if (!clientId) {
      setClientOffers([]);
      return;
    }
    try {
      const res = await fetchClientOffers({ client_id: clientId });
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
          ? res.data.results
          : [];
      setClientOffers(data);
    } catch (_err) {
      setClientOffers([]);
    }
  };

  useEffect(() => {
    loadVerticals();
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentClientId = searchParams.get('client_id') || '';
    const nextClientId = filterClient ? String(filterClient) : '';
    if (currentClientId === nextClientId) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    if (nextClientId) {
      nextParams.set('client_id', nextClientId);
    } else {
      nextParams.delete('client_id');
    }
    setSearchParams(nextParams, { replace: true });
  }, [filterClient, searchParams, setSearchParams]);

  useEffect(() => {
    loadClientOffers(current.client);
  }, [current.client]);

  useEffect(() => {
    let cancelled = false;

    const syncSharedAssets = async () => {
      const verticalIds = current.verticals || [];
      if (!verticalIds.length) {
        setCurrent((prev) => ({
          ...prev,
          client_vertical_assets: [],
          shared_vertical_assets: [],
        }));
        return;
      }

      setLoadingSharedAssets(true);
      try {
        const res = await fetchSharedVerticalCreativeAssets(verticalIds);
        if (cancelled) {
          return;
        }
        const fetchedRows = Array.isArray(res.data) ? res.data : [];
        const fetchedByVertical = new Map(
          fetchedRows.map((row) => [String(row.vertical), row])
        );

        setCurrent((prev) => {
          const selectedVerticalIds = new Set(verticalIds.map((value) => String(value)));
          const existingClientRows = prev.client_vertical_assets.filter(
            (row) => selectedVerticalIds.has(String(row.vertical))
          );
          const existingClientByVertical = new Map(
            existingClientRows.map((row) => [String(row.vertical), row])
          );
          const existingRows = prev.shared_vertical_assets.filter((row) => selectedVerticalIds.has(String(row.vertical)));
          const existingByVertical = new Map(
            existingRows.map((row) => [String(row.vertical), row])
          );
          const nextClientRows = verticalIds.map((verticalId) => {
            const key = String(verticalId);
            const existing = existingClientByVertical.get(key);
            if (existing) {
              return existing;
            }
            const detail = Array.isArray(prev.vertical_details)
              ? prev.vertical_details.find((item) => String(item.vertical) === key)
              : null;
            if (detail?.client_asset_folder_url) {
              return {
                ...EMPTY_CLIENT_VERTICAL_ASSET,
                vertical: verticalId,
                client_asset_folder_url: detail.client_asset_folder_url,
              };
            }
            return {
              ...EMPTY_CLIENT_VERTICAL_ASSET,
              vertical: verticalId,
            };
          });

          const nextRows = verticalIds.map((verticalId) => {
            const key = String(verticalId);
            const existing = existingByVertical.get(key);
            const fetched = fetchedByVertical.get(key);
            const existingHasValues = Boolean(
              existing?.id
                || existing?.general_asset_folder_url
                || existing?.recent_ad_examples_url
            );

            if (existingHasValues) {
              return existing;
            }

            if (fetched) {
              return {
                id: fetched.id,
                vertical: fetched.vertical,
                general_asset_folder_url: fetched.general_asset_folder_url || '',
                recent_ad_examples_url: fetched.recent_ad_examples_url || '',
              };
            }

            return {
              ...EMPTY_SHARED_VERTICAL_ASSET,
              vertical: verticalId,
            };
          });

          return {
            ...prev,
            client_vertical_assets: nextClientRows,
            shared_vertical_assets: nextRows,
          };
        });
      } catch (_err) {
        if (cancelled) {
          return;
        }
      } finally {
        if (!cancelled) {
          setLoadingSharedAssets(false);
        }
      }
    };

    syncSharedAssets();

    return () => {
      cancelled = true;
    };
  }, [current.verticals]);

  const handleOpen = (row = EMPTY_RECORD) => {
    setClientOffers([]);
    const nextClient = row.client || '';
    setCurrent({
      ...EMPTY_RECORD,
      ...row,
      client: nextClient,
      bath_manufacturer: Array.isArray(row.bath_manufacturer) ? row.bath_manufacturer : [],
      bath_manufacturer_label: row.bath_manufacturer_label || '',
      bath_manufacturer_options: Array.isArray(row.bath_manufacturer_options)
        ? row.bath_manufacturer_options
        : [],
      verticals: Array.isArray(row.verticals) ? row.verticals : [],
      client_vertical_assets: Array.isArray(row.client_vertical_assets)
        ? row.client_vertical_assets.map((item) => ({
          id: item.id,
          vertical: item.vertical || '',
          client_asset_folder_url: item.client_asset_folder_url || '',
        }))
        : [],
      shared_vertical_assets: Array.isArray(row.shared_vertical_assets)
        ? row.shared_vertical_assets.map((item) => ({
          id: item.id,
          vertical: item.vertical || '',
          general_asset_folder_url: item.general_asset_folder_url || '',
          recent_ad_examples_url: item.recent_ad_examples_url || '',
        }))
        : [],
    });
    setFieldErrors({});
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setCurrent(EMPTY_RECORD);
    setFieldErrors({});
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setFieldErrors({});
    const payload = {
      client: current.client,
      bath_manufacturer: current.bath_manufacturer || [],
      verticals: current.verticals,
      website_url: current.website_url,
      logo_url: current.logo_url,
      notes: current.notes,
      ai_script_instructions: current.ai_script_instructions || '',
      client_vertical_assets: current.client_vertical_assets
        .filter((item) => item.vertical)
        .map((item) => ({
          ...(item.id ? { id: item.id } : {}),
          vertical: item.vertical,
          client_asset_folder_url: item.client_asset_folder_url || '',
        })),
      shared_vertical_assets: current.shared_vertical_assets
        .filter((item) => item.vertical)
        .map((item) => ({
          ...(item.id ? { id: item.id } : {}),
          vertical: item.vertical,
          general_asset_folder_url: item.general_asset_folder_url || '',
          recent_ad_examples_url: item.recent_ad_examples_url || '',
        })),
    };
    try {
      if (current.id) {
        await updateClientCreativeKnowledge(current.id, payload);
      } else {
        await createClientCreativeKnowledge(payload);
      }
      handleClose();
      await loadRows();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to save creative knowledge'));
      setFieldErrors(getApiFieldErrors(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this creative knowledge record?')) return;
    setError('');
    try {
      await deleteClientCreativeKnowledge(id);
      await loadRows();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to delete creative knowledge'));
    }
  };

  const updateVerticalAssetRow = (index, field, value) => {
    setCurrent((prev) => ({
      ...prev,
      shared_vertical_assets: prev.shared_vertical_assets.map((row, rowIndex) => (
        rowIndex === index ? { ...row, [field]: value } : row
      )),
    }));
  };
  const updateClientVerticalAssetRow = (index, value) => {
    setCurrent((prev) => ({
      ...prev,
      client_vertical_assets: prev.client_vertical_assets.map((row, rowIndex) => (
        rowIndex === index ? { ...row, client_asset_folder_url: value } : row
      )),
    }));
  };

  const errorList = collectErrorMessages(fieldErrors);
  const clientVerticalAssetErrors = Array.isArray(fieldErrors.client_vertical_assets)
    ? fieldErrors.client_vertical_assets
    : [];
  const sharedVerticalAssetErrors = Array.isArray(fieldErrors.shared_vertical_assets)
    ? fieldErrors.shared_vertical_assets
    : [];
  const clientError = Array.isArray(fieldErrors.client) ? fieldErrors.client[0] : '';
  const verticalsError = Array.isArray(fieldErrors.verticals) ? fieldErrors.verticals[0] : '';
  const websiteError = Array.isArray(fieldErrors.website_url) ? fieldErrors.website_url[0] : '';
  const logoError = Array.isArray(fieldErrors.logo_url) ? fieldErrors.logo_url[0] : '';
  const notesError = Array.isArray(fieldErrors.notes) ? fieldErrors.notes[0] : '';
  const currentVerticalDetails = Array.isArray(current.vertical_details) ? current.vertical_details : [];
  const verticalDetailsById = new Map(currentVerticalDetails.map((detail) => [String(detail.vertical), detail]));
  const bathManufacturerError = Array.isArray(fieldErrors.bath_manufacturer) ? fieldErrors.bath_manufacturer[0] : '';
  const hasBathVertical = current.verticals.some((verticalId) => (
    verticals.find((item) => item.id === verticalId)?.vertical === 'Bath'
  ));
  const bathManufacturerOptions = Array.isArray(current.bath_manufacturer_options)
    && current.bath_manufacturer_options.length
    ? current.bath_manufacturer_options
    : BATH_MANUFACTURER_OPTIONS;
  const formatOfferLabel = (offer) => {
    const baseLabel = offer.display_label || offer.label || offer.name || `Offer ${offer.id}`;
    return offer.offer_type === 'financing' ? `Financing: ${baseLabel}` : `Ad: ${baseLabel}`;
  };
  const derivedOffersByVertical = new Map();
  clientOffers.forEach((offer) => {
    const verticalId = String(offer.vertical || '');
    if (!verticalId) return;
    if (!['offer', 'financing'].includes(offer.offer_type)) return;
    if (!derivedOffersByVertical.has(verticalId)) {
      derivedOffersByVertical.set(verticalId, []);
    }
    derivedOffersByVertical.get(verticalId).push(offer);
  });
  const handleUrlBlur = (field) => (event) => {
    const nextValue = normalizeUrl(event.target.value);
    setCurrent((prev) => ({ ...prev, [field]: nextValue }));
  };
  const handleSharedAssetUrlBlur = (index, field) => (event) => {
    updateVerticalAssetRow(index, field, normalizeUrl(event.target.value));
  };
  const handleClientAssetUrlBlur = (index) => (event) => {
    updateClientVerticalAssetRow(index, normalizeUrl(event.target.value));
  };

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h5" fontWeight={700}>
          Client Creative Knowledge
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows} disabled={loading}>
            Refresh
          </Button>
          {isStaff ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
              Add Record
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={clientOptions}
                loading={clientOptionsLoading}
                value={findClientOptionByValue(filterClient) || null}
                onInputChange={(_event, value) => handleClientSearch(value)}
                onChange={(_event, value) => setFilterClient(value?.value || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Client" placeholder="All clients" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Vertical"
                value={filterVertical}
                onChange={(event) => setFilterVertical(event.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {verticals.map((vertical) => (
                  <MenuItem key={vertical.id} value={vertical.id}>
                    {vertical.vertical}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Client, notes, link, offer"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" onClick={loadRows} disabled={loading} sx={{ height: '100%' }}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : rows.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
              <Typography variant="h6" color="text.secondary">No creative knowledge records yet</Typography>
              <Typography variant="body2" color="text.secondary">{isStaff ? 'Add a record to store brand context, asset links, and AI script instructions for each client.' : 'No records available yet.'}</Typography>
              {isStaff ? <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>Add Record</Button> : null}
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((row) => (
            <Card key={row.id} variant="outlined">
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Stack spacing={1.5}>
                  {/* Header row */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box>
                        <Typography fontWeight={700} variant="body1">{row.client_name || row.client}</Typography>
                        {row.parent_company_name ? <Typography variant="caption" color="text.secondary">{row.parent_company_name}</Typography> : null}
                      </Box>
                      {(row.vertical_details || []).map((d) => (
                        <Chip key={`v-${row.id}-${d.vertical}`} label={d.vertical_name} size="small" variant="outlined" />
                      ))}
                      {row.bath_manufacturer_label ? <Chip label={`Mfr: ${row.bath_manufacturer_label}`} size="small" color="info" variant="outlined" /> : null}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <ExternalLink href={row.website_url} label="Website" />
                      <ExternalLink href={row.logo_url} label="Logo" />
                      {isStaff ? (
                        <>
                          <IconButton size="small" onClick={() => handleOpen(row)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </>
                      ) : null}
                    </Stack>
                  </Stack>

                  {/* Notes preview */}
                  {row.notes ? (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {row.notes}
                    </Typography>
                  ) : null}

                  {/* AI Script Instructions indicator */}
                  {row.ai_script_instructions ? (
                    <Chip label="AI Script Instructions configured" size="small" color="success" variant="outlined" sx={{ alignSelf: 'flex-start' }} />
                  ) : null}

                  {/* Vertical details grid */}
                  {(row.vertical_details || []).length ? (
                    <Grid container spacing={1.5}>
                      {row.vertical_details.map((detail) => (
                        <Grid item xs={12} md={6} lg={4} key={`${row.id}-vd-${detail.vertical}`}>
                          <Card variant="outlined" sx={{ p: 1.5, height: '100%', bgcolor: 'action.hover' }}>
                            <Stack spacing={0.75}>
                              <Typography variant="subtitle2">{detail.vertical_name}</Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {(detail.offers || []).length
                                  ? detail.offers.map((offer) => (
                                    <Chip key={`${row.id}-o-${offer.id}`} size="small" label={formatOfferLabel(offer)} sx={{ height: 20, '& .MuiChip-label': { px: 0.5 } }} />
                                  ))
                                  : <Typography variant="caption" color="text.secondary">No offers</Typography>}
                              </Stack>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                <ExternalLink href={detail.client_asset_folder_url} label="Client Assets" />
                                <ExternalLink href={detail.shared_assets?.general_asset_folder_url} label="Shared Assets" />
                                <ExternalLink href={detail.shared_assets?.recent_ad_examples_url} label="Recent Ads" />
                              </Stack>
                            </Stack>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>{current.id ? 'Edit Creative Knowledge' : 'Add Creative Knowledge'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {errorList.length > 0 ? (
              <Alert severity="error">
                <Stack spacing={0.5}>
                  {errorList.map((message, index) => (
                    <Typography key={`${message}-${index}`} variant="body2">
                      {message}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            ) : null}

            <Autocomplete
              options={clientOptions}
              loading={clientOptionsLoading}
              value={findClientOptionByValue(current.client) || null}
              onInputChange={(_event, value) => handleClientSearch(value)}
              onChange={(_event, value) => setCurrent((prev) => ({
                ...prev,
                client: value?.value || '',
                bath_manufacturer: [],
                bath_manufacturer_label: '',
                bath_manufacturer_options: prev.bath_manufacturer_options,
                verticals: [],
                vertical_details: [],
                client_vertical_assets: [],
                shared_vertical_assets: [],
              }))}
              renderInput={(params) => (
                <TextField {...params} label="Client" required error={Boolean(clientError)} helperText={clientError || ''} />
              )}
            />

            <Autocomplete
              multiple
              options={verticals}
              getOptionLabel={(option) => option.vertical || ''}
              value={verticals.filter((vertical) => current.verticals.includes(vertical.id))}
              onChange={(_event, value) => setCurrent((prev) => ({
                ...prev,
                verticals: value.map((item) => item.id),
              }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Verticals"
                  placeholder="Select client verticals"
                  error={Boolean(verticalsError)}
                  helperText={verticalsError || ''}
                />
              )}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Website URL"
                  type="url"
                  fullWidth
                  value={current.website_url}
                  onChange={(event) => setCurrent((prev) => ({ ...prev, website_url: event.target.value }))}
                  onBlur={handleUrlBlur('website_url')}
                  error={Boolean(websiteError)}
                  helperText={websiteError || ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Logo URL"
                  type="url"
                  fullWidth
                  value={current.logo_url}
                  onChange={(event) => setCurrent((prev) => ({ ...prev, logo_url: event.target.value }))}
                  onBlur={handleUrlBlur('logo_url')}
                  error={Boolean(logoError)}
                  helperText={logoError || ''}
                />
              </Grid>
            </Grid>

            {hasBathVertical ? (
              <Autocomplete
                multiple
                options={bathManufacturerOptions}
                getOptionLabel={(option) => option.label || ''}
                value={bathManufacturerOptions.filter((option) => current.bath_manufacturer.includes(option.value))}
                onChange={(_event, value) => setCurrent((prev) => ({
                  ...prev,
                  bath_manufacturer: value.map((item) => item.value),
                }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Bath Manufacturers"
                    error={Boolean(bathManufacturerError)}
                    helperText={bathManufacturerError || 'Stored on the client record for Bath vertical planning.'}
                  />
                )}
              />
            ) : null}

            <TextField
              label="Notes"
              multiline
              minRows={4}
              value={current.notes}
              onChange={(event) => setCurrent((prev) => ({ ...prev, notes: event.target.value }))}
              placeholder="Internal creative notes, positioning, visual constraints, brand direction, territory nuances..."
              error={Boolean(notesError)}
              helperText={notesError || ''}
            />

            <TextField
              label="AI Script Instructions"
              multiline
              minRows={3}
              value={current.ai_script_instructions || ''}
              onChange={(event) => setCurrent((prev) => ({ ...prev, ai_script_instructions: event.target.value }))}
              placeholder="Client-specific rules for the AI script generator (e.g. 'Always mention 25-year warranty', 'Premium luxury tone', 'Never reference competitor brands')..."
              helperText="These instructions are automatically included when generating ad scripts for this client."
            />

            <Divider />

            <Typography variant="subtitle1" fontWeight={700}>
              Vertical Details
            </Typography>

            {current.verticals.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Select one or more client verticals to see offers and edit client-specific or shared asset links for each vertical.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {current.verticals.map((verticalId, index) => {
                  const detail = verticalDetailsById.get(String(verticalId));
                  const verticalLabel = verticals.find((item) => item.id === verticalId)?.vertical || detail?.vertical_name || verticalId;
                  const offers = derivedOffersByVertical.get(String(verticalId))
                    || (Array.isArray(detail?.offers) ? detail.offers : []);
                  const clientAssetRow = current.client_vertical_assets.find(
                    (r) => String(r.vertical) === String(verticalId),
                  ) || EMPTY_CLIENT_VERTICAL_ASSET;
                  const clientAssetIndex = current.client_vertical_assets.indexOf(clientAssetRow);
                  const sharedAssetRow = current.shared_vertical_assets.find(
                    (r) => String(r.vertical) === String(verticalId),
                  ) || EMPTY_SHARED_VERTICAL_ASSET;
                  const sharedAssetIndex = current.shared_vertical_assets.indexOf(sharedAssetRow);
                  const clientAssetRowErrors = clientVerticalAssetErrors[index] || {};
                  const clientAssetError = Array.isArray(clientAssetRowErrors.client_asset_folder_url)
                    ? clientAssetRowErrors.client_asset_folder_url[0]
                    : '';
                  const sharedAssetRowErrors = sharedVerticalAssetErrors[index] || {};
                  const generalAssetError = Array.isArray(sharedAssetRowErrors.general_asset_folder_url)
                    ? sharedAssetRowErrors.general_asset_folder_url[0]
                    : '';
                  const recentAdsError = Array.isArray(sharedAssetRowErrors.recent_ad_examples_url)
                    ? sharedAssetRowErrors.recent_ad_examples_url[0]
                    : '';
                  return (
                    <Card key={`derived-offers-${verticalId}`} variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        <Typography variant="subtitle2">{verticalLabel}</Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                          {offers.length ? offers.map((offer) => (
                            <Chip
                              key={`derived-offer-${verticalId}-${offer.id}`}
                              size="small"
                              label={formatOfferLabel(offer)}
                              variant="outlined"
                            />
                          )) : (
                            <Typography variant="body2" color="text.secondary">
                              No client offers found for this vertical.
                            </Typography>
                          )}
                        </Stack>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Client Asset Folder URL"
                              type="url"
                              value={clientAssetRow.client_asset_folder_url}
                              onChange={(event) => updateClientVerticalAssetRow(clientAssetIndex, event.target.value)}
                              onBlur={handleClientAssetUrlBlur(clientAssetIndex)}
                              error={Boolean(clientAssetError)}
                              helperText={clientAssetError || ''}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="General Asset Folder URL"
                              type="url"
                              value={sharedAssetRow.general_asset_folder_url}
                              onChange={(event) => updateVerticalAssetRow(sharedAssetIndex, 'general_asset_folder_url', event.target.value)}
                              onBlur={handleSharedAssetUrlBlur(sharedAssetIndex, 'general_asset_folder_url')}
                              error={Boolean(generalAssetError)}
                              helperText={generalAssetError || 'Shared across all clients in this vertical.'}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Recent Ad Examples URL"
                              type="url"
                              value={sharedAssetRow.recent_ad_examples_url}
                              onChange={(event) => updateVerticalAssetRow(sharedAssetIndex, 'recent_ad_examples_url', event.target.value)}
                              onBlur={handleSharedAssetUrlBlur(sharedAssetIndex, 'recent_ad_examples_url')}
                              error={Boolean(recentAdsError)}
                              helperText={recentAdsError || ''}
                            />
                          </Grid>
                        </Grid>
                      </Stack>
                    </Card>
                  );
                })}
              </Stack>
            )}
            {loadingSharedAssets ? (
              <Typography variant="body2" color="text.secondary">
                Loading shared assets for selected verticals...
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !current.client}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
